// Analysis Engine - Main orchestrator for student analysis
import { aggregateStudentData } from './analysisDataService';
import { generateCognitiveProfile } from './cognitiveAnalyzer';
import { generateBehavioralProfile } from './behavioralAnalyzer';
import { generateRecommendations } from './recommendationEngine';

/**
 * Generates complete student analysis
 * @param {string} userId - Student's user ID
 * @param {Array} batchIds - Array of batch IDs
 * @param {Object} timeRange - Optional time range {start, end}
 * @param {Function} onProgress - Optional callback for progressive updates
 * @returns {Promise<Object>} Complete analysis data
 */
export async function generateStudentAnalysis(userId, batchIds = [], timeRange = null, onProgress = null) {
  try {
    console.log('Starting student analysis for:', userId);

    // Helper to report progress
    const reportProgress = (step, data) => {
      if (onProgress && typeof onProgress === 'function') {
        onProgress({ step, data, timestamp: new Date() });
      }
    };

    // Step 1: Aggregate all data
    reportProgress('aggregating', { message: 'Fetching your data...' });
    const aggregatedData = await aggregateStudentData(userId, batchIds, timeRange);
    console.log('Data aggregation complete');
    reportProgress('aggregated', { message: 'Data loaded successfully' });

    // Step 2: Generate cognitive profile with user profile integration
    reportProgress('analyzing_cognitive', { message: 'Analyzing learning patterns...' });
    const cognitiveProfile = generateCognitiveProfile(
      aggregatedData.testData?.testResults || [],
      aggregatedData.revisionData,
      aggregatedData.profileSettings
    );
    console.log('Cognitive profile generated with profile data integration');
    reportProgress('cognitive_complete', { cognitiveProfile });

    // Step 3: Analyze test performance (quick analysis)
    reportProgress('analyzing_tests', { message: 'Analyzing test performance...' });
    const testPerformance = analyzeTestPerformance(aggregatedData.testData);
    reportProgress('tests_complete', { testPerformance });

    // Step 4: Generate behavioral profile
    reportProgress('analyzing_behavior', { message: 'Analyzing study behavior...' });
    const behavioralProfile = generateBehavioralProfile(aggregatedData);
    console.log('Behavioral profile generated');
    reportProgress('behavior_complete', { behavioralProfile });

    // Step 5: Analyze assignments
    reportProgress('analyzing_assignments', { message: 'Analyzing assignments...' });
    const assignmentAnalysis = analyzeAssignments(aggregatedData.assignmentData);
    reportProgress('assignments_complete', { assignmentAnalysis });

    // Step 6: Analyze attendance
    reportProgress('analyzing_attendance', { message: 'Analyzing attendance...' });
    const attendanceAnalysis = analyzeAttendance(aggregatedData.attendanceData);
    reportProgress('attendance_complete', { attendanceAnalysis });

    // Step 7: Analyze doubts
    reportProgress('analyzing_doubts', { message: 'Analyzing doubt activity...' });
    const doubtAnalysis = analyzeDoubts(aggregatedData.doubtData);
    reportProgress('doubts_complete', { doubtAnalysis });

    // Step 8: Analyze revision
    reportProgress('analyzing_revision', { message: 'Analyzing revision patterns...' });
    const revisionAnalysis = analyzeRevision(aggregatedData.revisionData);
    reportProgress('revision_complete', { revisionAnalysis });

    // Step 9: Analyze study streak
    const studyStreakAnalysis = aggregatedData.studyStreakData;

    // Step 10: Analyze todo tasks
    const todoTaskAnalysis = behavioralProfile.todoTaskAnalysis;

    // Step 11: Analyze course enrollment
    const courseEnrollmentAnalysis = behavioralProfile.courseEngagement;

    // Step 12: Analyze counselling
    const counsellingAnalysis = behavioralProfile.counsellingImpact;

    // Step 13: Analyze bookmarks
    reportProgress('analyzing_bookmarks', { message: 'Analyzing bookmarks...' });
    const bookmarksAnalysis = analyzeBookmarks(aggregatedData.bookmarksData);
    reportProgress('bookmarks_complete', { bookmarksAnalysis });

    // Step 14: Generate recommendations
    reportProgress('generating_recommendations', { message: 'Generating personalized recommendations...' });
    const recommendations = generateRecommendations({
      cognitiveProfile,
      behavioralProfile,
      testPerformance,
      assignmentAnalysis,
      attendanceAnalysis,
      doubtAnalysis,
      revisionAnalysis
    });
    console.log('Recommendations generated');
    reportProgress('recommendations_complete', { recommendations });

    // Step 15: Calculate comparison with batch (placeholder)
    const comparisonWithBatch = {
      scorePercentile: 50,
      attendancePercentile: 50,
      engagementPercentile: 50,
      batchAverage: {
        score: 60,
        attendance: 75,
        assignmentCompletion: 80
      }
    };

    // Step 16: Track progress
    const progressTracking = calculateProgressTracking(aggregatedData.testData);

    // Compile complete analysis
    const completeAnalysis = {
      userId,
      generatedAt: new Date(),
      timeRange: aggregatedData.timeRange,
      cognitiveProfile,
      behavioralProfile,
      testPerformance,
      assignmentAnalysis,
      attendanceAnalysis,
      doubtAnalysis,
      revisionAnalysis,
      studyStreakAnalysis,
      todoTaskAnalysis,
      courseEnrollmentAnalysis,
      counsellingAnalysis,
      bookmarksAnalysis,
      recommendations,
      comparisonWithBatch,
      progressTracking,
      profileSettings: aggregatedData.profileSettings,
      testBehaviorAnalysis: behavioralProfile.testBehaviorAnalysis // Include detailed test behavior
    };

    console.log('Analysis complete');
    return {
      success: true,
      data: completeAnalysis
    };

  } catch (error) {
    console.error('Error generating student analysis:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Analyzes test performance
 */
function analyzeTestPerformance(testData) {
  if (!testData || !testData.testResults || testData.testResults.length === 0) {
    return {
      totalTests: 0,
      averageScore: 0,
      averageAccuracy: 0,
      scoreProgression: [],
      sectionalPerformance: [],
      topicWisePerformance: []
    };
  }

  const tests = testData.testResults;
  const totalTests = tests.length;

  // Helper to get score from test (supports multiple field names)
  const getTestScore = (test) => {
    return test.percentageScore || test.score || test.percentage || test.accuracy || 0;
  };

  // Calculate averages
  const totalScore = tests.reduce((sum, test) => sum + getTestScore(test), 0);
  const totalAccuracy = tests.reduce((sum, test) => sum + (test.accuracy || test.percentageScore || test.score || 0), 0);
  const averageScore = totalTests > 0 ? Math.round(totalScore / totalTests) : 0;
  const averageAccuracy = totalTests > 0 ? Math.round(totalAccuracy / totalTests) : 0;

  // Score progression
  const scoreProgression = tests
    .filter(test => test.completedAt || test.createdAt || test.submittedAt)
    .sort((a, b) => {
      const dateA = new Date(a.completedAt || a.createdAt || a.submittedAt);
      const dateB = new Date(b.completedAt || b.createdAt || b.submittedAt);
      return dateA - dateB;
    })
    .map(test => ({
      date: test.completedAt || test.createdAt || test.submittedAt,
      score: getTestScore(test),
      testName: test.testName || test.name || 'Test'
    }));

  // Sectional performance
  const sectionMap = {};
  tests.forEach(test => {
    if (test.sectionalResults) {
      test.sectionalResults.forEach(section => {
        if (!sectionMap[section.name]) {
          sectionMap[section.name] = {
            section: section.name,
            scores: [],
            totalScore: 0,
            count: 0
          };
        }
        const sectionScore = section.score || section.percentage || section.accuracy || 0;
        sectionMap[section.name].scores.push(sectionScore);
        sectionMap[section.name].totalScore += sectionScore;
        sectionMap[section.name].count++;
      });
    }
  });

  const sectionalPerformance = Object.values(sectionMap).map(section => {
    const averageScore = section.count > 0 ? Math.round(section.totalScore / section.count) : 0;
    
    // Determine trend
    let trend = 'stable';
    if (section.scores.length >= 3) {
      const recent = section.scores.slice(-3);
      const older = section.scores.slice(0, -3);
      const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
      const olderAvg = older.length > 0 ? older.reduce((a, b) => a + b, 0) / older.length : recentAvg;
      
      if (recentAvg > olderAvg * 1.1) trend = 'improving';
      else if (recentAvg < olderAvg * 0.9) trend = 'declining';
    }

    return {
      section: section.section,
      averageScore,
      trend
    };
  });

  // Topic-wise performance
  const topicMap = {};
  tests.forEach(test => {
    if (test.detailedResults) {
      test.detailedResults.forEach(question => {
        const topic = question.topic || 'General';
        if (!topicMap[topic]) {
          topicMap[topic] = {
            topic,
            correct: 0,
            total: 0,
            totalTime: 0
          };
        }
        topicMap[topic].total++;
        if (question.isCorrect) topicMap[topic].correct++;
        topicMap[topic].totalTime += question.timeSpent || 0;
      });
    }
  });

  const topicWisePerformance = Object.values(topicMap).map(topic => ({
    topic: topic.topic,
    accuracy: Math.round((topic.correct / topic.total) * 100),
    questionsAttempted: topic.total,
    averageTime: Math.round(topic.totalTime / topic.total)
  }));

  return {
    totalTests,
    averageScore,
    averageAccuracy,
    scoreProgression,
    sectionalPerformance,
    topicWisePerformance
  };
}

/**
 * Analyzes assignment submissions
 */
function analyzeAssignments(assignmentData) {
  if (!assignmentData || !assignmentData.submissions || assignmentData.submissions.length === 0) {
    return {
      totalAssignments: 0,
      completionRate: 100,
      averageSubmissionDelay: 0,
      qualityScore: 0,
      submissionPattern: 'on-time',
      recentSubmissions: []
    };
  }

  const submissions = assignmentData.submissions;
  const totalAssignments = submissions.length;
  const completionRate = 100; // All fetched submissions are completed

  // Calculate submission delays
  let totalDelay = 0;
  let earlyCount = 0;
  let onTimeCount = 0;
  let lateCount = 0;

  submissions.forEach(submission => {
    const submitted = new Date(submission.submittedAt);
    const deadline = new Date(submission.deadline);
    const delayHours = (submitted - deadline) / (1000 * 60 * 60);

    totalDelay += Math.max(0, delayHours);

    if (delayHours < -24) earlyCount++;
    else if (delayHours <= 0) onTimeCount++;
    else lateCount++;
  });

  const averageSubmissionDelay = Math.round(totalDelay / totalAssignments);

  // Determine submission pattern
  let submissionPattern = 'mixed';
  if (earlyCount > totalAssignments * 0.6) submissionPattern = 'early';
  else if (onTimeCount > totalAssignments * 0.6) submissionPattern = 'on-time';
  else if (lateCount > totalAssignments * 0.6) submissionPattern = 'late';

  // Quality score (placeholder - would need grading data)
  const qualityScore = 75;

  // Recent submissions
  const recentSubmissions = submissions
    .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
    .slice(0, 5)
    .map(s => {
      const submitted = new Date(s.submittedAt);
      const deadline = new Date(s.deadline);
      let status = 'on-time';
      if (submitted < deadline.setHours(deadline.getHours() - 24)) status = 'early';
      else if (submitted > deadline) status = 'late';

      return {
        assignmentId: s.assignmentId,
        submittedAt: s.submittedAt,
        deadline: s.deadline,
        status
      };
    });

  return {
    totalAssignments,
    completionRate,
    averageSubmissionDelay,
    qualityScore,
    submissionPattern,
    recentSubmissions
  };
}

/**
 * Analyzes attendance
 */
function analyzeAttendance(attendanceData) {
  if (!attendanceData || !attendanceData.records || attendanceData.records.length === 0) {
    return {
      overallAttendance: 0,
      batchWiseAttendance: [],
      attendancePattern: {
        consistentDays: [],
        frequentAbsenceDays: []
      },
      trend: 'stable'
    };
  }

  const records = attendanceData.records;
  const totalAttendance = records.reduce((sum, record) => sum + (record.attendance || 0), 0);
  const overallAttendance = Math.round(totalAttendance / records.length);

  const batchWiseAttendance = records.map(record => ({
    batchId: record.batchId,
    batchName: record.batchName,
    attendance: record.attendance
  }));

  return {
    overallAttendance,
    batchWiseAttendance,
    attendancePattern: {
      consistentDays: ['Monday', 'Wednesday', 'Friday'],
      frequentAbsenceDays: []
    },
    trend: 'stable'
  };
}

/**
 * Analyzes doubt activity
 */
function analyzeDoubts(doubtData) {
  if (!doubtData || !doubtData.doubts || doubtData.doubts.length === 0) {
    return {
      totalDoubts: 0,
      averageResolutionTime: 0,
      topConfusionAreas: [],
      doubtFrequency: 'low',
      engagementScore: 0
    };
  }

  const doubts = doubtData.doubts;
  const totalDoubts = doubts.length;

  // Calculate average resolution time
  let totalResolutionTime = 0;
  let resolvedCount = 0;

  doubts.forEach(doubt => {
    if (doubt.isResolved && doubt.resolvedAt) {
      const created = new Date(doubt.createdAt);
      const resolved = new Date(doubt.resolvedAt);
      const hours = (resolved - created) / (1000 * 60 * 60);
      totalResolutionTime += hours;
      resolvedCount++;
    }
  });

  const averageResolutionTime = resolvedCount > 0 
    ? Math.round(totalResolutionTime / resolvedCount) 
    : 0;

  // Top confusion areas
  const topicMap = {};
  doubts.forEach(doubt => {
    const topic = doubt.topic || doubt.subject || 'General';
    topicMap[topic] = (topicMap[topic] || 0) + 1;
  });

  const topConfusionAreas = Object.entries(topicMap)
    .map(([topic, doubtCount]) => ({ topic, doubtCount }))
    .sort((a, b) => b.doubtCount - a.doubtCount)
    .slice(0, 5);

  // Determine frequency
  let doubtFrequency = 'low';
  if (totalDoubts >= 20) doubtFrequency = 'high';
  else if (totalDoubts >= 10) doubtFrequency = 'moderate';

  // Engagement score
  const engagementScore = Math.min(100, totalDoubts * 5);

  return {
    totalDoubts,
    averageResolutionTime,
    topConfusionAreas,
    doubtFrequency,
    engagementScore
  };
}

/**
 * Analyzes revision activity
 */
function analyzeRevision(revisionData) {
  if (!revisionData || (!revisionData.practiceSessions && !revisionData.wrongQuestions)) {
    return {
      totalPracticeSessions: 0,
      totalQuestionsRevised: 0,
      totalWrongQuestions: 0,
      totalSkippedQuestions: 0,
      revisionEffectiveness: 0,
      mostRevisedTopics: [],
      leastRevisedTopics: [],
      revisionConsistency: 0
    };
  }

  const sessions = revisionData.practiceSessions || [];
  const wrongQuestions = revisionData.wrongQuestions || [];
  
  // Only count actual practice/revision sessions (not synthetic ones from wrong questions)
  const actualPracticeSessions = sessions.filter(s => !s.isRevisionSession);
  const totalPracticeSessions = actualPracticeSessions.length;

  // Use unique counts from data service
  const totalWrongQuestions = revisionData.uniqueWrongQuestions || 0;
  const totalSkippedQuestions = revisionData.uniqueSkippedQuestions || 0;

  // Calculate total questions revised (ONLY from actual practice sessions, not wrong/skipped)
  const totalQuestionsRevised = actualPracticeSessions.reduce((sum, session) => {
    return sum + (session.questionCount || 0);
  }, 0);

  // Calculate effectiveness (based on accuracy improvement from actual practice sessions only)
  const totalAccuracy = actualPracticeSessions.reduce((sum, session) => {
    return sum + (session.accuracy || 0);
  }, 0);
  const revisionEffectiveness = actualPracticeSessions.length > 0 
    ? Math.round(totalAccuracy / actualPracticeSessions.length) 
    : 0;

  // Most revised topics (from actual practice sessions and wrong questions separately)
  const topicMap = {};
  
  // Add from actual practice sessions only
  actualPracticeSessions.forEach(session => {
    const topic = session.topic || 'General';
    if (!topicMap[topic]) {
      topicMap[topic] = { 
        sessionCount: 0, 
        totalAccuracy: 0,
        wrongCount: 0,
        skippedCount: 0,
        questionCount: 0,
        revisedCount: 0
      };
    }
    topicMap[topic].sessionCount++;
    topicMap[topic].totalAccuracy += session.accuracy || 0;
    topicMap[topic].revisedCount += session.questionCount || 0;
  });

  // Add unique wrong/skipped questions (these are NOT revised yet, just identified)
  const questionsWithAttempts = revisionData.questionsWithAttempts || [];
  questionsWithAttempts.forEach(q => {
    const topic = q.topic || q.subject || 'General';
    if (!topicMap[topic]) {
      topicMap[topic] = { 
        sessionCount: 0, 
        totalAccuracy: 0,
        wrongCount: 0,
        skippedCount: 0,
        questionCount: 0,
        revisedCount: 0
      };
    }
    // Count unique questions only
    if (q.wrongCount > 0) {
      topicMap[topic].wrongCount++;
    }
    if (q.skippedCount > 0) {
      topicMap[topic].skippedCount++;
    }
  });

  const mostRevisedTopics = Object.entries(topicMap)
    .map(([topic, data]) => ({
      topic,
      sessionCount: data.sessionCount,
      revisedCount: data.revisedCount,
      wrongCount: data.wrongCount,
      skippedCount: data.skippedCount,
      improvementRate: data.sessionCount > 0 ? Math.round(data.totalAccuracy / data.sessionCount) : 0
    }))
    .sort((a, b) => (b.revisedCount + b.wrongCount + b.skippedCount) - (a.revisedCount + a.wrongCount + a.skippedCount))
    .slice(0, 5);

  // Revision consistency (based ONLY on actual practice sessions)
  const revisionConsistency = Math.min(100, totalPracticeSessions * 10);

  return {
    totalPracticeSessions,
    totalQuestionsRevised, // Only actual revised questions
    totalWrongQuestions,
    totalSkippedQuestions,
    revisionEffectiveness,
    mostRevisedTopics,
    leastRevisedTopics: [],
    revisionConsistency,
    questionsWithAttempts: revisionData.questionsWithAttempts || []
  };
}

/**
 * Analyzes bookmarks
 */
function analyzeBookmarks(bookmarksData) {
  if (!bookmarksData || bookmarksData.totalBookmarks === 0) {
    return {
      totalBookmarks: 0,
      questionBookmarks: 0,
      resourceBookmarks: 0,
      bookmarksByTopic: [],
      bookmarkUsageRate: 0,
      mostBookmarkedTopics: [],
      bookmarkPattern: 'minimal',
      averageRevisitTime: 0
    };
  }

  const questionBookmarks = bookmarksData.questionBookmarks?.length || 0;
  const resourceBookmarks = bookmarksData.bookmarks?.length || 0;
  const totalBookmarks = questionBookmarks + resourceBookmarks;

  // Analyze by topic
  const topicMap = {};
  [...(bookmarksData.questionBookmarks || []), ...(bookmarksData.bookmarks || [])].forEach(bookmark => {
    const topic = bookmark.topic || bookmark.subject || 'General';
    topicMap[topic] = (topicMap[topic] || 0) + 1;
  });

  const bookmarksByTopic = Object.entries(topicMap)
    .map(([topic, count]) => ({ topic, count }))
    .sort((a, b) => b.count - a.count);

  const mostBookmarkedTopics = bookmarksByTopic.slice(0, 5).map(b => b.topic);

  // Determine pattern
  let bookmarkPattern = 'minimal';
  if (totalBookmarks >= 50) bookmarkPattern = 'excessive';
  else if (totalBookmarks >= 20) bookmarkPattern = 'strategic';

  return {
    totalBookmarks,
    questionBookmarks,
    resourceBookmarks,
    bookmarksByTopic,
    bookmarkUsageRate: 50, // Placeholder
    mostBookmarkedTopics,
    bookmarkPattern,
    averageRevisitTime: 24 // Placeholder: 24 hours
  };
}

/**
 * Calculates progress tracking
 */
function calculateProgressTracking(testData) {
  if (!testData || !testData.testResults || testData.testResults.length < 2) {
    return {
      lastMonthImprovement: {
        score: 0,
        accuracy: 0,
        attendance: 0,
        engagement: 0
      },
      milestones: []
    };
  }

  const tests = testData.testResults.sort((a, b) => 
    new Date(a.completedAt) - new Date(b.completedAt)
  );

  // Calculate last month improvement
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  const recentTests = tests.filter(t => new Date(t.completedAt) >= oneMonthAgo);
  const olderTests = tests.filter(t => new Date(t.completedAt) < oneMonthAgo);

  let scoreImprovement = 0;
  let accuracyImprovement = 0;

  if (recentTests.length > 0 && olderTests.length > 0) {
    const recentAvgScore = recentTests.reduce((sum, t) => sum + t.score, 0) / recentTests.length;
    const olderAvgScore = olderTests.reduce((sum, t) => sum + t.score, 0) / olderTests.length;
    scoreImprovement = olderAvgScore > 0 ? Math.round(((recentAvgScore - olderAvgScore) / olderAvgScore) * 100) : 0;

    const recentAvgAccuracy = recentTests.reduce((sum, t) => sum + t.accuracy, 0) / recentTests.length;
    const olderAvgAccuracy = olderTests.reduce((sum, t) => sum + t.accuracy, 0) / olderTests.length;
    accuracyImprovement = olderAvgAccuracy > 0 ? Math.round(((recentAvgAccuracy - olderAvgAccuracy) / olderAvgAccuracy) * 100) : 0;
  }

  return {
    lastMonthImprovement: {
      score: scoreImprovement,
      accuracy: accuracyImprovement,
      attendance: 0,
      engagement: 0
    },
    milestones: []
  };
}
